import { useEffect, useState } from "react";

/**
 * Instagram Reel UI overlay rendered on top of a phone screen image.
 *
 * Scaling: all dimensions are em-relative; the root font-size is set via the
 * cqi unit (3.6% of the phone screen's inline size). The phone screen container
 * must declare `container-type: inline-size` for this to work.
 *
 * Visual style: white text/icons with a soft drop-shadow so the UI stays
 * readable over any image; Follow button is the only solid surface (white
 * pill, black text), reading as the CTA the way Instagram styles it.
 *
 * Count animation: when `active` flips true, the like/comment/share counts
 * wait for the phone slide-in to settle, then tick up from 0 to their final
 * values over ~1.4s.
 */

export interface ReelMeta {
  time: string;
  username: string;
  isVerified: boolean;
  caption: string;
  likes: string;
  comments: string;
  shares: string;
  audio: string;
  avatarSrc: string;
}

interface Props {
  meta: ReelMeta;
  isActive: boolean;
}

const parseCount = (s: string) => {
  const n = parseInt(s.replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
};

const IconWrap = ({
  size,
  children,
}: {
  size: string;
  children: React.ReactNode;
}) => (
  <span
    aria-hidden="true"
    style={{ width: size, height: size, display: "inline-block", lineHeight: 0 }}
  >
    {children}
  </span>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="100%" height="100%">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l8.84 8.84 8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const CommentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="100%" height="100%">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
  </svg>
);

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="100%" height="100%">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const DotsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <circle cx="5" cy="12" r="1.7" />
    <circle cx="12" cy="12" r="1.7" />
    <circle cx="19" cy="12" r="1.7" />
  </svg>
);

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="100%" height="100%">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="3.6" />
  </svg>
);

const MusicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="100%" height="100%">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="2.6" />
    <circle cx="18" cy="16" r="2.6" />
  </svg>
);

const VerifiedIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M12 1.6l1.7 1.9 2.5-.4 1 2.3 2.3 1-.4 2.5 1.9 1.7-1.9 1.7.4 2.5-2.3 1-1 2.3-2.5-.4L12 19.6l-1.7-1.9-2.5.4-1-2.3-2.3-1 .4-2.5L3 10.6l1.9-1.7-.4-2.5 2.3-1 1-2.3 2.5.4z" />
    <path d="M9.5 12.3l1.8 1.8 3.5-3.5" stroke="#000" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const ActionItem = ({
  icon,
  count,
}: {
  icon: React.ReactNode;
  count: string;
}) => (
  <div className="flex flex-col items-center" style={{ gap: "0.15em" }}>
    <IconWrap size="2em">{icon}</IconWrap>
    <span className="font-medium" style={{ fontSize: "0.7em", lineHeight: 1 }}>
      {count}
    </span>
  </div>
);

const InstagramReelOverlay = ({ meta, isActive }: Props) => {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const targetLikes = parseCount(meta.likes);
  const targetComments = parseCount(meta.comments);
  const targetShares = parseCount(meta.shares);

  // Initial state: 0 unless reduced motion (then jump straight to final values).
  const [counts, setCounts] = useState(() =>
    prefersReducedMotion
      ? { likes: meta.likes, comments: meta.comments, shares: meta.shares }
      : { likes: "0", comments: "0", shares: "0" },
  );

  useEffect(() => {
    if (!isActive) return;
    if (prefersReducedMotion) {
      setCounts({
        likes: meta.likes,
        comments: meta.comments,
        shares: meta.shares,
      });
      return;
    }

    // Wait for the phone slide-in (~1500ms + max enterDelay 180ms) to settle,
    // then run the count-up. Total stagger across all 4 phones still feels like
    // one synchronized moment because every overlay starts at the same delay.
    const COUNT_DELAY_MS = 1900;
    const COUNT_DURATION_MS = 1400;

    let rafId = 0;
    let delayId: ReturnType<typeof setTimeout> | null = null;
    let alive = true;

    delayId = setTimeout(() => {
      delayId = null;
      const start = performance.now();
      const tick = (now: number) => {
        if (!alive) return;
        const t = Math.min(1, (now - start) / COUNT_DURATION_MS);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        setCounts({
          likes: Math.round(targetLikes * eased).toLocaleString(),
          comments: Math.round(targetComments * eased).toLocaleString(),
          shares: Math.round(targetShares * eased).toLocaleString(),
        });
        if (t < 1) {
          rafId = requestAnimationFrame(tick);
        }
      };
      rafId = requestAnimationFrame(tick);
    }, COUNT_DELAY_MS);

    return () => {
      alive = false;
      if (delayId) clearTimeout(delayId);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    isActive,
    prefersReducedMotion,
    targetLikes,
    targetComments,
    targetShares,
    meta.likes,
    meta.comments,
    meta.shares,
  ]);

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none text-white select-none"
      style={{
        fontSize: "3.6cqi",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        filter: "drop-shadow(0 0.06em 0.16em rgba(0,0,0,0.5))",
      }}
    >
      {/* Top row: time (left) + camera (right) */}
      <div
        className="absolute left-0 right-0 flex justify-between items-center"
        style={{ top: "2.5%", paddingLeft: "5%", paddingRight: "5%" }}
      >
        <span className="font-bold" style={{ fontSize: "1.15em" }}>
          {meta.time}
        </span>
        <IconWrap size="1.6em">
          <CameraIcon />
        </IconWrap>
      </div>

      {/* Right action column, lower-third */}
      <div
        className="absolute flex flex-col items-center"
        style={{ right: "4%", bottom: "22%", gap: "1.1em" }}
      >
        <ActionItem icon={<HeartIcon />} count={counts.likes} />
        <ActionItem icon={<CommentIcon />} count={counts.comments} />
        <ActionItem icon={<ShareIcon />} count={counts.shares} />
        <IconWrap size="1.7em">
          <DotsIcon />
        </IconWrap>
      </div>

      {/* Bottom block: avatar + name + Follow, caption, audio */}
      <div
        className="absolute"
        style={{ left: "4%", right: "18%", bottom: "3.5%" }}
      >
        {/* Identity row */}
        <div className="flex items-center" style={{ gap: "0.55em" }}>
          <img
            src={meta.avatarSrc}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="rounded-full shrink-0 object-cover"
            style={{ width: "2em", height: "2em" }}
          />
          <div
            className="flex items-center min-w-0"
            style={{ gap: "0.25em" }}
          >
            <span
              className="font-semibold truncate"
              style={{ fontSize: "0.92em", lineHeight: 1.1 }}
            >
              {meta.username}
            </span>
            {meta.isVerified && (
              <IconWrap size="0.85em">
                <VerifiedIcon />
              </IconWrap>
            )}
          </div>
          <button
            type="button"
            tabIndex={-1}
            className="bg-white text-black font-semibold rounded-full shrink-0"
            style={{
              fontSize: "0.78em",
              padding: "0.25em 0.85em",
              lineHeight: 1.15,
              marginLeft: "0.4em",
            }}
          >
            Follow
          </button>
        </div>

        {/* Caption — single line, ellipsis if it overflows */}
        <p
          className="truncate"
          style={{ fontSize: "0.85em", lineHeight: 1.25, marginTop: "0.5em" }}
        >
          {meta.caption}
        </p>

        {/* Audio row */}
        <div
          className="flex items-center"
          style={{ gap: "0.4em", marginTop: "0.45em" }}
        >
          <IconWrap size="0.9em">
            <MusicIcon />
          </IconWrap>
          <span
            className="truncate"
            style={{ fontSize: "0.72em", lineHeight: 1.2 }}
          >
            {meta.audio}
          </span>
        </div>
      </div>
    </div>
  );
};

export default InstagramReelOverlay;
